/**
 * Pure FIFO registry that matches inbound indexer trade events to in-flight
 * trade submissions, so the trade hooks can fire a success toast the instant
 * the indexer publishes the trade — before the slow Turnkey signature poll.
 *
 * This file is PURE: no NATS, no platform APIs, no timers. The NATS wiring and
 * proto decode live in `user-trade-watch.ts`; this module only owns the
 * matching + single-consumption invariants, so they can be unit-tested in
 * isolation (the zero-missed / zero-duplicate correctness proof lives here).
 *
 * Invariants (the safety contract):
 *  - An inbound event is matched to a pending trade ONLY when EXACTLY ONE
 *    pending entry is eligible for its `(wallet, market, outcomeIdx, side)` key.
 *    With 2+ identical-tuple trades in flight we cannot tell which submission
 *    the event belongs to (AMM carries no signature; BC does but the leg does
 *    not yet know its own), so we DEFER all of them to the authoritative
 *    per-trade Turnkey confirm rather than risk crediting a landed sibling's
 *    event to a different (possibly reverted) trade. This is the core
 *    anti-mis-attribution guarantee — never show success for a trade that did
 *    not land, never double-count.
 *  - A pending trade is matched AT MOST ONCE (its `resolve` fires once).
 *  - A redelivered event (same `sequenceId`) is ignored (JetStream at-least-once).
 *  - Only events at/after the trade's submit time (minus a small clock-skew
 *    tolerance) can match — a stale event from a prior trade cannot satisfy a
 *    later submission.
 */

/** Normalized trade activity — the shape both `MarketTrade` (bonding
 *  curve) and `AmmUserActivity` (graduated cp-swap) decode into. */
export interface NormalizedTradeActivity {
  /** Trader wallet (base58). */
  actor: string;
  /** Market pubkey (base58). */
  market: string;
  outcomeIdx: number;
  side: 'buy' | 'sell';
  /** On-chain block time, unix SECONDS. */
  ts: bigint;
  /** Base58 tx signature — present for bonding-curve trades, NULL for AMM
   *  (AmmUserActivity carries no signature; Turnkey reconciles it later). */
  signature: string | null;
  /** Hex of the event's `sequenceId` bytes — global dedup key. */
  sequenceIdHex: string;
}

export interface TradeMatchCriteria {
  wallet: string;
  market: string;
  outcomeIdx: number;
  side: 'buy' | 'sell';
  /** Earliest acceptable event `ts` (unix seconds), already skew-adjusted. */
  sinceTsSec: bigint;
}

export interface TradeMatch {
  /** Base58 signature, or null for AMM (reconciled from Turnkey later). */
  signature: string | null;
  ts: bigint;
}

interface PendingEntry {
  id: number;
  criteria: TradeMatchCriteria;
  resolve: (m: TradeMatch | null) => void;
  consumed: boolean;
}

const SEEN_SEQ_CAP = 512;

function keyOf(
  c: { market: string; outcomeIdx: number; side: 'buy' | 'sell' } & {
    wallet?: string;
    actor?: string;
  },
): string {
  const wallet = c.wallet ?? c.actor ?? '';
  return `${wallet}|${c.market}|${c.outcomeIdx}|${c.side}`;
}

export class PendingTradeRegistry {
  private readonly queues = new Map<string, PendingEntry[]>();
  /** Bounded FIFO set of consumed sequenceIds (JetStream redelivery dedup). */
  private readonly seenSeq = new Set<string>();
  private readonly seenSeqOrder: string[] = [];
  private nextId = 1;

  /** Enqueue a pending trade. Returns its id + a promise that resolves when a
   *  matching event arrives (or never, until `cancel` is called by the caller). */
  enqueue(criteria: TradeMatchCriteria): { id: number; matched: Promise<TradeMatch | null> } {
    const id = this.nextId++;
    let resolve!: (m: TradeMatch | null) => void;
    const matched = new Promise<TradeMatch | null>((r) => {
      resolve = r;
    });
    const entry: PendingEntry = {
      id,
      criteria,
      resolve: (m): void => resolve(m),
      consumed: false,
    };
    const k = keyOf(criteria);
    const q = this.queues.get(k);
    if (q === undefined) this.queues.set(k, [entry]);
    else q.push(entry);
    return { id, matched };
  }

  /** Remove a pending entry (called when the trade settles). Resolves its
   *  promise with null if it never matched, so any awaiter unblocks. */
  cancel(id: number): void {
    for (const [k, q] of this.queues) {
      const idx = q.findIndex((e) => e.id === id);
      if (idx === -1) continue;
      const [entry] = q.splice(idx, 1);
      if (entry !== undefined && !entry.consumed) {
        entry.consumed = true;
        entry.resolve(null); // unblock awaiters
      }
      if (q.length === 0) this.queues.delete(k);
      return;
    }
  }

  /** Route an inbound (already decoded + normalized) trade event to the SINGLE
   *  eligible pending trade for its key. Resolves nothing (defers to Turnkey)
   *  when 0 or 2+ entries are eligible. Idempotent on redelivered events. */
  onActivity(act: NormalizedTradeActivity): void {
    if (this.seenSeq.has(act.sequenceIdHex)) return;
    // Consume every valid decoded delivery before consulting mutable pending
    // queues. Otherwise an initially unmatched/ambiguous event could be
    // redelivered after queue membership changes and incorrectly satisfy a
    // later submission.
    this.markSeen(act.sequenceIdHex);

    const q = this.queues.get(keyOf(act));
    if (q === undefined) return;

    const eligible = q.filter((e) => !e.consumed && act.ts >= e.criteria.sinceTsSec);
    // 0 eligible → not ours / not yet armed. 2+ eligible → ambiguous: we cannot
    // attribute this event to a specific submission, so DEFER every same-tuple
    // trade to its own authoritative Turnkey confirm (no eager match). Only an
    // unambiguous single match is safe to credit.
    if (eligible.length !== 1) return;
    const entry = eligible[0];
    if (entry === undefined) return;

    entry.consumed = true;
    const i = q.indexOf(entry);
    if (i !== -1) q.splice(i, 1);
    if (q.length === 0) this.queues.delete(keyOf(act));

    entry.resolve({ signature: act.signature, ts: act.ts });
  }

  private markSeen(seq: string): void {
    this.seenSeq.add(seq);
    this.seenSeqOrder.push(seq);
    if (this.seenSeqOrder.length > SEEN_SEQ_CAP) {
      const evicted = this.seenSeqOrder.shift();
      if (evicted !== undefined) this.seenSeq.delete(evicted);
    }
  }

  /** Test-only: number of pending entries (across all keys). */
  get pendingCount(): number {
    let n = 0;
    for (const q of this.queues.values()) n += q.length;
    return n;
  }
}
